import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../requestOptions';

export type AiSuggestionDecisionItem = {
  suggestion_id: number;
  original_value: string | null;
  override_value: string | null;
  is_user_override: boolean;
};

type AcceptSuggestionsRequest = {
  suggestions: AiSuggestionDecisionItem[];
  applied_by: string;
};

type RejectSuggestionsRequest = {
  suggestions: AiSuggestionDecisionItem[];
  rejected_by: string;
};

/** One per-item failure from a partial (HTTP 207) reject batch. */
export type RejectSuggestionsError = {
  suggestion_id: number;
  error: string;
};

/** Post-decision snapshot of a suggestion, as AiSuggestionAcceptanceService::buildUpdatedItemResponse builds it. */
export type RejectSuggestionsUpdatedItem = {
  id: number;
  subscription_id: number;
  product_id: number;
  assignment_id: number;
  user_decision_status: string | null;
  status: string;
  decided_by: string | null;
  decided_at: string | null;
  applied_value: string | null;
  suggested_value: string | null;
};

/**
 * Body of POST atts/acendaai/reject_suggestions/{subscription_id}
 * (AcendaAiRejectSuggestionController -> AiSuggestionAcceptanceService::rejectBatch).
 * The endpoint answers 200 when `errors` is empty and 207 for a partial success,
 * so callers that batch several suggestions must read `errors` to know which
 * ones actually failed - a non-error response is not proof every item succeeded.
 */
export type RejectSuggestionsResponse = {
  subscription_id: number;
  processed_count: number;
  successful_count: number;
  error_count: number;
  errors: RejectSuggestionsError[];
  updated_items: RejectSuggestionsUpdatedItem[];
  immediate_update?: boolean;
};

type BaseArgs = {
  subscriptionId: number;
  suggestions: AiSuggestionDecisionItem[];
};

export async function acceptAiSuggestions(
  scope: RequestScope,
  args: BaseArgs & { appliedBy: string },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<void>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!Array.isArray(args.suggestions) || args.suggestions.length === 0) {
      throw new Error('At least one suggestion is required');
    }

    const body: AcceptSuggestionsRequest = {
      suggestions: args.suggestions,
      applied_by: args.appliedBy,
    };

    const res = await scopedChannelApi(scope).post(`atts/acendaai/accept_suggestions/${args.subscriptionId}`, body, {
      ...options,
      headers: {
        'x-astur-subscription-id': args.subscriptionId.toString(),
      },
    });

    return { data: undefined, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export async function rejectAiSuggestions(
  scope: RequestScope,
  args: BaseArgs & { rejectedBy: string },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<RejectSuggestionsResponse>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!Array.isArray(args.suggestions) || args.suggestions.length === 0) {
      throw new Error('At least one suggestion is required');
    }

    const body: RejectSuggestionsRequest = {
      suggestions: args.suggestions,
      rejected_by: args.rejectedBy,
    };

    const res = await scopedChannelApi(scope).post<RejectSuggestionsResponse>(`atts/acendaai/reject_suggestions/${args.subscriptionId}`, body, {
      ...options,
      headers: {
        'x-astur-subscription-id': args.subscriptionId.toString(),
      },
    });

    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
